import { useTranslation } from "react-i18next"
import {
  type EnhancedAnalyticsSummaryProps,
  type CalculatedMetrics,
} from "../../../types/analytics"
import dayjs from "dayjs"
import { TruncatedText } from "../../ui/TruncatedText"
import { InfoIcon } from "lucide-react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

interface ProductTotals {
  vtoEvents: number
  addToCartUsers: number
  uniqueUsers: number
  addToCartEvents: number
}

export const EnhancedAnalyticsSummary: React.FC<EnhancedAnalyticsSummaryProps> = ({
  analyticsItems,
  productAnalytics,
  startOfCycle,
  showOverlay = false,
}) => {
  const { t } = useTranslation()
  const cycleStartDate = dayjs(startOfCycle)
  const filteredAnalytics = analyticsItems.filter(
    (item) => dayjs(item.date).isAfter(cycleStartDate) || dayjs(item.date).isSame(cycleStartDate)
  )

  const totalUniqueUsers = filteredAnalytics.reduce((sum, item) => sum + item.unique_users, 0)
  const totalVtoUsers = filteredAnalytics.reduce((sum, item) => sum + item.vto_users, 0)
  const totalVtoEvents = filteredAnalytics.reduce((sum, item) => sum + item.vto_events, 0)
  const totalAddToCartUsers = filteredAnalytics.reduce(
    (sum, item) => sum + item.add_to_cart_users,
    0
  )
  const totalCartActions = filteredAnalytics.reduce((sum, item) => sum + item.add_to_cart, 0)
  const rawEngagedUsers = filteredAnalytics.reduce((sum, item) => sum + item.engaged_users, 0)
  // Engaged users cannot exceed total unique users (engaged users are a subset of total users)
  const totalEngagedUsers = Math.min(rawEngagedUsers, totalUniqueUsers)
  const totalAddToCartEvents = filteredAnalytics.reduce((sum, item) => sum + item.add_to_cart, 0)

  const vtoConversionRate = totalUniqueUsers > 0 ? (totalVtoUsers / totalUniqueUsers) * 100 : 0
  const cartConversionRate =
    totalUniqueUsers > 0 ? (totalAddToCartUsers / totalUniqueUsers) * 100 : 0
  const vtoToCartRate = totalVtoUsers > 0 ? (totalAddToCartUsers / totalVtoUsers) * 100 : 0
  // Conversion rate: VTO to Cart
  const addToCartConversionRate = vtoToCartRate

  const averageVtoSessions = totalVtoUsers > 0 ? totalVtoEvents / totalVtoUsers : 0
  const averageItemsPerCart = totalAddToCartUsers > 0 ? totalCartActions / totalAddToCartUsers : 0
  const avgVtoEventsPerUser = averageVtoSessions
  const avgSkusPerCart = averageItemsPerCart

  const productVtoTotals = productAnalytics.reduce((acc, item) => {
    if (!item.product_title) return acc

    if (!acc[item.product_title])
      acc[item.product_title] = {
        vtoEvents: 0,
        addToCartUsers: 0,
        uniqueUsers: 0,
        addToCartEvents: 0,
      }

    acc[item.product_title].vtoEvents += item.vto_events
    acc[item.product_title].addToCartUsers += item.add_to_cart_users
    acc[item.product_title].uniqueUsers += item.unique_users
    acc[item.product_title].addToCartEvents += item.add_to_cart
    return acc
  }, {} as Record<string, ProductTotals>)

  const topVtoProduct =
    Object.entries(productVtoTotals).sort(([, a], [, b]) => b.vtoEvents - a.vtoEvents)[0]?.[0] ??
    null

  const topAddToCartProduct =
    Object.entries(productVtoTotals).sort(
      ([, a], [, b]) => b.addToCartEvents - a.addToCartEvents
    )[0]?.[0] ?? null

  const bestConversionProduct =
    Object.entries(productVtoTotals)
      .map(([name, totals]) => ({
        name,
        conversionRate:
          totals.uniqueUsers > 0 ? (totals.addToCartUsers / totals.uniqueUsers) * 100 : 0,
      }))
      .sort((a, b) => b.conversionRate - a.conversionRate)[0]?.name ?? null

  const totalProductsWithData = Object.keys(productVtoTotals).length

  const metrics: CalculatedMetrics = {
    vtoConversionRate,
    cartConversionRate,
    vtoToCartRate,
    averageVtoSessions,
    averageItemsPerCart,
    totalUniqueUsers,
    totalCartActions,
    totalVtoEvents,
    topVtoProduct,
    bestConversionProduct,
    totalProductsWithData,

    addToCartConversionRate,
    avgVtoEventsPerUser,
    totalEngagedUsers,
    totalAddToCartEvents,
    topAddToCartProduct,
    avgSkusPerCart,
    vtoConversionRateTrend: undefined,
    vtoConversionRateTrendValue: undefined,
    addToCartConversionRateTrend: undefined,
    addToCartConversionRateTrendValue: undefined,
    avgVtoEventsPerUserTrend: undefined,
    avgVtoEventsPerUserTrendValue: undefined,
    totalUniqueUsersTrend: undefined,
    totalUniqueUsersTrendValue: undefined,
    totalEngagedUsersTrend: undefined,
    totalEngagedUsersTrendValue: undefined,
    totalVtoEventsTrend: undefined,
    totalVtoEventsTrendValue: undefined,
    totalAddToCartEventsTrend: undefined,
    totalAddToCartEventsTrendValue: undefined,
    avgSkusPerCartTrend: undefined,
    avgSkusPerCartTrendValue: undefined,
  }

  const formatPercentage = (value: number | undefined) =>
    value !== undefined ? `${value.toFixed(1)}%` : "0.0%"
  const formatNumber = (value: number | undefined) =>
    value !== undefined ? value.toLocaleString() : "0"

  const MetricCard: React.FC<{
    title: string
    value: string | React.ReactNode
    valueClassName?: string
    subtitle?: string
    trend?: "up" | "down" | "stable"
    trendValue?: string
    variant?: "primary" | "secondary"
    tooltip?: string
  }> = ({
    title,
    value,
    valueClassName,
    subtitle,
    trend,
    trendValue,
    variant = "primary",
    tooltip,
  }) => {
    const { t } = useTranslation()
    const isPrimary = variant === "primary"

    return (
      <div
        className={`rounded-lg border p-4 ${
          isPrimary
            ? "bg-gradient-to-br from-muted/50 to-muted/30 border-border/50"
            : "bg-gradient-to-br from-muted/50 to-muted/30 border-border/50"
        }`}
      >
        <div
          className={`flex items-center gap-1 text-[13px] font-medium ${
            isPrimary ? "text-foreground" : "text-muted-foreground"
          }`}
        >
          {title}
          {tooltip && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center">
                    <InfoIcon className="w-4 h-4 cursor-pointer text-muted-foreground hover:text-foreground" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>{tooltip}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div
          className={valueClassName || "mt-2 text-2xl font-semibold tracking-tight text-foreground"}
        >
          {value}
        </div>
        {subtitle && <div className="mt-1 text-xs text-muted-foreground">{subtitle}</div>}
        {trend && trendValue && (
          <div className="mt-2 flex items-center gap-1">
            <div
              className={`text-xs font-medium ${
                trend === "up"
                  ? "text-green-400"
                  : trend === "down"
                  ? "text-red-400"
                  : "text-muted-foreground"
              }`}
            >
              {trend === "up" ? "↗" : trend === "down" ? "↘" : "→"} {trendValue}
            </div>
            <div className="text-xs text-muted-foreground">
              {t("analytics.summary.vsLastPeriod")}
            </div>
          </div>
        )}
      </div>
    )
  }

  if (showOverlay)
    return (
      <div className="p-4 bg-card rounded border">
        <h3 className="mb-3 text-lg font-semibold tracking-tight text-foreground">
          {t("analytics.summary.loading")}
        </h3>
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="col-span-1">
              <div className="p-3 bg-muted rounded h-[100px]" />
            </div>
          ))}
        </div>
      </div>
    )

  return (
    <div className="p-6 bg-card rounded-lg border">
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold tracking-tight text-foreground">
              {t("analytics.summary.title")}
            </h3>
            <p className="text-sm text-muted-foreground">{t("analytics.summary.subtitle")}</p>
          </div>
          <div className="text-right">
            <div className="text-xs text-muted-foreground">
              {t("analytics.summary.dataFrom", { date: dayjs(startOfCycle).format("MMM D, YYYY") })}
            </div>
            <div className="text-xs text-muted-foreground">
              {t("analytics.summary.lastUpdated", { time: dayjs().format("MMM D, h:mm A") })}
            </div>
          </div>
        </div>
        <div>
          <h4 className="mb-3 text-base font-semibold tracking-tight text-foreground">
            {t("analytics.summary.performanceMetrics")}
          </h4>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-1">
              <MetricCard
                title={t("analytics.summary.vtoConversionRate")}
                value={formatPercentage(metrics.vtoConversionRate)}
                trend={metrics.vtoConversionRateTrend}
                trendValue={formatPercentage(metrics.vtoConversionRateTrendValue)}
                variant="primary"
                tooltip={t("analytics.summary.vtoConversionRateTooltip")}
              />
            </div>
            <div className="col-span-1">
              <MetricCard
                title={t("analytics.summary.addToCartConversion")}
                value={formatPercentage(metrics.addToCartConversionRate)}
                trend={metrics.addToCartConversionRateTrend}
                trendValue={formatPercentage(metrics.addToCartConversionRateTrendValue)}
                variant="secondary"
                tooltip={t("analytics.summary.addToCartConversionTooltip")}
              />
            </div>
            <div className="col-span-1">
              <MetricCard
                title={t("analytics.summary.avgVtoEventsPerUser")}
                value={formatNumber(metrics.avgVtoEventsPerUser)}
                trend={metrics.avgVtoEventsPerUserTrend}
                trendValue={formatNumber(metrics.avgVtoEventsPerUserTrendValue)}
                variant="primary"
                tooltip={t("analytics.summary.avgVtoEventsPerUserTooltip")}
              />
            </div>
          </div>
        </div>

        {/* User Behavior */}
        <div>
          <h4 className="mb-3 text-base font-semibold tracking-tight text-foreground">
            {t("analytics.summary.userBehavior")}
          </h4>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-1">
              <MetricCard
                title={t("analytics.summary.totalUsers")}
                value={formatNumber(metrics.totalUniqueUsers)}
                trend={metrics.totalUniqueUsersTrend}
                trendValue={formatNumber(metrics.totalUniqueUsersTrendValue)}
                variant="secondary"
                tooltip={t("analytics.summary.totalUsersTooltip")}
              />
            </div>
            <div className="col-span-1">
              <MetricCard
                title={t("analytics.summary.totalVtoEvents")}
                value={formatNumber(metrics.totalVtoEvents)}
                trend={metrics.totalVtoEventsTrend}
                trendValue={formatNumber(metrics.totalVtoEventsTrendValue)}
                variant="primary"
                tooltip={t("analytics.summary.totalVtoEventsTooltip")}
              />
            </div>
            <div className="col-span-1">
              <MetricCard
                title={t("analytics.summary.totalAddToCartEvents")}
                value={formatNumber(metrics.totalAddToCartEvents)}
                trend={metrics.totalAddToCartEventsTrend}
                trendValue={formatNumber(metrics.totalAddToCartEventsTrendValue)}
                variant="secondary"
                tooltip={t("analytics.summary.totalAddToCartEventsTooltip")}
              />
            </div>
          </div>
        </div>

        {/* Product Insights */}
        <div>
          <h4 className="mb-3 text-base font-semibold tracking-tight text-foreground">
            {t("analytics.summary.productInsights")}
          </h4>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-1">
              <MetricCard
                title={t("analytics.summary.topVtoProduct")}
                value={
                  <TruncatedText
                    text={metrics.topVtoProduct || "N/A"}
                    maxLength={25}
                    className="text-foreground"
                  />
                }
                valueClassName="mt-2 text-base font-semibold leading-tight text-foreground sm:text-lg"
                variant="primary"
                tooltip={t("analytics.summary.topVtoProductTooltip")}
              />
            </div>
            <div className="col-span-1">
              <MetricCard
                title={t("analytics.summary.topAddToCartProduct")}
                value={
                  <TruncatedText
                    text={metrics.topAddToCartProduct || "N/A"}
                    maxLength={25}
                    className="text-foreground"
                  />
                }
                valueClassName="mt-2 text-base font-semibold leading-tight text-foreground sm:text-lg"
                variant="secondary"
                tooltip={t("analytics.summary.topAddToCartProductTooltip")}
              />
            </div>
            <div className="col-span-1">
              <MetricCard
                title={t("analytics.summary.avgSkusPerCart")}
                value={formatNumber(metrics.avgSkusPerCart)}
                trend={metrics.avgSkusPerCartTrend}
                trendValue={formatNumber(metrics.avgSkusPerCartTrendValue)}
                variant="primary"
                tooltip={t("analytics.summary.avgSkusPerCartTooltip")}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
